<script setup lang="ts">
import {
  PhActivity,
  PhBinary,
  PhBookOpenText,
  PhCrosshairSimple,
  PhDatabase,
  PhGraph,
  PhPlugsConnected,
  PhSignOut,
  PhTerminalWindow,
  PhWarningDiamond
} from "@phosphor-icons/vue"
import { computed, onBeforeUnmount, onMounted } from "vue"
import { RouterLink, useRoute, useRouter } from "vue-router"
import StatusBadge from "../components/StatusBadge.vue"
import { useDashboardApi } from "../composables/useDashboardApi"
import type { DashboardEventStreamConnection } from "../composables/useDashboardApi"
import { useAuthStore } from "../stores/auth"
import { scenarioItems, useDashboardStore } from "../stores/dashboard"
import { useUiStore } from "../stores/ui"
import { dashboardEnvironment } from "../utils/env"

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const dashboard = useDashboardStore()
const ui = useUiStore()
let fallbackRefreshTimer: number | undefined
let reconnectTimer: number | undefined
let streamConnection: DashboardEventStreamConnection | undefined
let streamGeneration = 0
let streamReconnectAttempt = 0
let streamRefreshTimer: number | undefined

const fallbackRefreshIntervalMillis = 30_000
const streamRefreshDebounceMillis = 250

const navItems = [
  { icon: PhCrosshairSimple, label: "Dashboard", to: "/" },
  { icon: PhGraph, label: "Nodes", to: "/nodes" },
  { icon: PhPlugsConnected, label: "Connections", to: "/connections" },
  { icon: PhDatabase, label: "Sessions", to: "/sessions" },
  { icon: PhBinary, label: "Dataplane", to: "/relay" },
  { icon: PhActivity, label: "Events", to: "/events" },
  { icon: PhWarningDiamond, label: "Recommendations", to: "/recommendations" },
  { icon: PhTerminalWindow, label: "Diagnostics", to: "/diagnostics" },
  { icon: PhBookOpenText, label: "Documentation", to: "/documentation" }
]

const fixtureMode = computed(() => dashboardEnvironment.dataMode === "fixtures")

function isCurrent(to: string) {
  return to === "/" ? route.path === "/" : route.path.startsWith(to)
}

onMounted(() => {
  void refreshDashboard({ silent: false })
  if (!fixtureMode.value) {
    connectDashboardStream()
  }
})

onBeforeUnmount(() => {
  stopDashboardStream()
  stopFallbackRefresh()
  if (streamRefreshTimer) {
    window.clearTimeout(streamRefreshTimer)
    streamRefreshTimer = undefined
  }
})

async function refreshDashboard(options: { force?: boolean; indicator?: boolean; silent?: boolean } = {}) {
  const { indicator = true, ...refreshOptions } = options
  const task = () => dashboard.refreshFromApi(auth.dashboardToken, refreshOptions)

  if (indicator) {
    await ui.runDashboardRefresh(task)
    return
  }

  await task()
}

function connectDashboardStream() {
  const generation = ++streamGeneration
  streamConnection?.close()
  streamConnection = useDashboardApi().openDashboardEventStream(auth.dashboardToken, {
    onClose() {
      if (generation !== streamGeneration) {
        return
      }
      streamConnection = undefined
      startFallbackRefresh()
      scheduleStreamReconnect()
    },
    onError() {
      if (generation !== streamGeneration) {
        return
      }
      startFallbackRefresh()
      scheduleStreamReconnect()
    },
    onMessage(message) {
      if (generation !== streamGeneration || message.type !== "dashboard.updated") {
        return
      }
      scheduleStreamRefresh()
    },
    onOpen() {
      if (generation !== streamGeneration) {
        return
      }
      streamReconnectAttempt = 0
      stopFallbackRefresh()
    }
  })
}

function scheduleStreamRefresh() {
  if (streamRefreshTimer) {
    return
  }

  streamRefreshTimer = window.setTimeout(() => {
    streamRefreshTimer = undefined
    void refreshDashboard({ force: true, indicator: false, silent: true })
  }, streamRefreshDebounceMillis)
}

function scheduleStreamReconnect() {
  if (reconnectTimer) {
    return
  }

  const delayMillis = Math.min(30_000, 1000 * 2 ** Math.min(streamReconnectAttempt, 5))
  streamReconnectAttempt += 1
  reconnectTimer = window.setTimeout(() => {
    reconnectTimer = undefined
    if (!fixtureMode.value) {
      connectDashboardStream()
    }
  }, delayMillis)
}

function startFallbackRefresh() {
  if (fallbackRefreshTimer || fixtureMode.value) {
    return
  }

  fallbackRefreshTimer = window.setInterval(() => {
    void refreshDashboard({ force: true, indicator: false, silent: true })
  }, fallbackRefreshIntervalMillis)
}

function stopFallbackRefresh() {
  if (!fallbackRefreshTimer) {
    return
  }

  window.clearInterval(fallbackRefreshTimer)
  fallbackRefreshTimer = undefined
}

function stopDashboardStream() {
  streamGeneration += 1
  if (reconnectTimer) {
    window.clearTimeout(reconnectTimer)
    reconnectTimer = undefined
  }
  streamConnection?.close()
  streamConnection = undefined
}

async function logout() {
  stopDashboardStream()
  auth.logout()
  await router.push("/auth")
}
</script>

<template>
  <UDashboardGroup class="min-h-screen bg-(--dash-canvas) text-(--dash-ink)" data-dashboard-layout>
    <UDashboardSidebar class="border-b border-(--dash-line) bg-[rgba(7,7,7,0.96)] lg:w-72 lg:border-b-0 lg:border-r">
      <div class="flex h-full flex-col">
        <div
          class="grid grid-cols-[minmax(0,1fr)_2.25rem] items-center gap-3 border-b border-(--dash-line) px-3 py-4 lg:grid-cols-1"
        >
          <RouterLink
            to="/"
            class="vhs-glitch-trigger grid min-w-0 grid-cols-[auto_auto_minmax(0,1fr)] items-center gap-x-3 px-2"
            aria-label="Undetect Control Plane dashboard"
          >
            <span class="dashboard-logo dashboard-logo--sidebar" aria-hidden="true" />
            <USeparator decorative orientation="vertical" size="xs" class="opacity-20" />
            <span class="dashboard-sidebar-brand-title vhs-glitch-text" data-vhs-text="CONTROL PLANE">
              Control Plane
            </span>
          </RouterLink>
          <UDashboardSidebarToggle
            class="dashboard-icon-button dashboard-icon-button--strong justify-self-end lg:hidden"
          />
        </div>

        <nav class="grid gap-1 px-3 py-4" aria-label="Dashboard navigation">
          <RouterLink
            v-for="item in navItems"
            :key="item.to"
            :to="item.to"
            class="dashboard-focus vhs-glitch-trigger group grid grid-cols-[20px_minmax(0,1fr)] items-center gap-3 border border-transparent px-3 py-2.5 text-sm font-medium transition"
            :class="
              isCurrent(item.to)
                ? 'border-(--dash-line-strong) bg-[rgba(241,243,242,0.07)] text-(--dash-ink)'
                : 'text-(--dash-muted) hover:border-(--dash-line) hover:bg-[rgba(241,243,242,0.035)] hover:text-(--dash-ink)'
            "
          >
            <component :is="item.icon" :size="18" />
            <span class="vhs-glitch-text truncate" :data-vhs-text="item.label">{{ item.label }}</span>
          </RouterLink>
        </nav>

        <div class="mt-auto grid gap-3 border-t border-(--dash-line) p-4">
          <div class="border border-(--dash-line) bg-[rgba(241,243,242,0.025)] p-3">
            <div class="flex items-center justify-between gap-3">
              <span class="dashboard-label">Session</span>
              <StatusBadge status="active" />
            </div>
          </div>

          <USelect
            v-if="fixtureMode"
            v-model="dashboard.scenario"
            :items="scenarioItems"
            aria-label="Fixture state"
            label-key="label"
            size="sm"
            :ui="{ trailingIcon: 'text-(--dash-dim)' }"
            value-key="value"
          />

          <UTooltip :text="auth.sessionExpiryLabel">
            <UButton
              color="neutral"
              variant="ghost"
              size="sm"
              block
              :icon="PhSignOut"
              class="dashboard-button dashboard-button--sidebar-sign-out vhs-glitch-trigger"
              @click="logout"
            >
              <span class="vhs-glitch-text" data-vhs-text="SIGN OUT">Sign out</span>
            </UButton>
          </UTooltip>
        </div>
      </div>
    </UDashboardSidebar>

    <UDashboardPanel class="min-w-0 flex-1">
      <UDashboardNavbar
        class="border-b border-(--dash-line) bg-[rgba(12,13,13,0.96)] px-4 md:px-6 lg:hidden"
        :toggle="{ class: 'dashboard-icon-button dashboard-icon-button--strong' }"
      />
      <UDashboardToolbar class="dashboard-top-toolbar" aria-label="Dashboard toolbar" />

      <main class="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-4 py-5 md:px-6 lg:px-8">
        <div class="dashboard-surface">
          <slot />
        </div>
      </main>
    </UDashboardPanel>
  </UDashboardGroup>
</template>
